/* Clay Maker Lab — cart drawer.
   Adds, removes and re-renders through the Section Rendering API: every cart call asks for
   `sections=cart-drawer-section` and we swap the returned markup in. No page reloads.
   All handlers are delegated from the document, so they survive those swaps. */

(function () {
  'use strict';

  var SECTION = 'cart-drawer-section';
  var WRAPPER = '#shopify-section-' + SECTION;
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var lastTrigger = null;
  var busy = false;

  function drawer() {
    return document.querySelector('[data-cml-cart]');
  }

  function panel() {
    var d = drawer();
    return d && d.querySelector('.cml-cart__panel');
  }

  /* ----------------------------------------------------------- open / close */

  function isOpen() {
    var d = drawer();
    return !!d && d.classList.contains('is-open');
  }

  function open(trigger) {
    var d = drawer();
    var p = panel();
    if (!d || !p) return;

    lastTrigger = trigger || document.activeElement;
    d.classList.add('is-open');
    p.setAttribute('aria-hidden', 'false');
    var backdrop = d.querySelector('.cml-cart__backdrop');
    if (backdrop) backdrop.hidden = false;
    document.body.classList.add('cml-cart-open');
    window.requestAnimationFrame(function () {
      p.focus();
    });
    normaliseQuantities();
  }

  function close() {
    var d = drawer();
    var p = panel();
    if (!d || !p) return;

    d.classList.remove('is-open');
    p.setAttribute('aria-hidden', 'true');
    var backdrop = d.querySelector('.cml-cart__backdrop');
    if (backdrop) backdrop.hidden = true;
    document.body.classList.remove('cml-cart-open');
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
    lastTrigger = null;
  }

  /* --------------------------------------------------------------- rendering */

  // The open state is a class on `[data-cml-cart]`, and the panel and backdrop inside it
  // carry the rest of it. The Section Rendering API returns that whole subtree re-rendered
  // and always closed — the section cannot know the drawer is open. Replacing the wrapper's
  // innerHTML therefore swapped an open drawer for a closed one on every cart call. So the
  // root, the panel and the scrolling body stay put, and only their contents are replaced.
  function swap(sections) {
    var html = sections && sections[SECTION];
    var wrapper = document.querySelector(WRAPPER);
    if (!html || !wrapper) return;

    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var freshRoot = parsed.querySelector('[data-cml-cart]');
    var freshPanel = freshRoot && freshRoot.querySelector('.cml-cart__panel');
    var live = drawer();
    var livePanel = panel();

    // Nothing live worth preserving (drawer absent, or markup we do not recognise):
    // fall back to the wholesale swap.
    if (!live || !livePanel || !freshPanel) {
      var fresh = parsed.querySelector(WRAPPER);
      wrapper.innerHTML = fresh ? fresh.innerHTML : html;
      syncCount();
      return;
    }

    // syncCount() reads the count off the root, and the root is the node we are keeping.
    live.setAttribute('data-cart-count', freshRoot.getAttribute('data-cart-count') || '0');

    var hadFocus = livePanel.contains(document.activeElement);

    // `.cml-cart__body` is the scroll container (overflow-y: auto), so the element itself
    // has to survive or the list jumps to the top on every add and remove.
    var liveBody = livePanel.querySelector('.cml-cart__body');
    var freshBody = freshPanel.querySelector('.cml-cart__body');
    if (liveBody && freshBody) {
      var offset = liveBody.scrollTop;
      liveBody.innerHTML = freshBody.innerHTML;
      // A shorter list clamps this on its own; the assignment only matters when it doesn't.
      liveBody.scrollTop = offset;
    }

    swapFoot(livePanel, freshPanel);

    // Whatever was focused inside the replaced markup is gone now — the upsell button that
    // was just clicked, usually. Keep focus in the dialog so the tab trap still has a home.
    if (hadFocus && !livePanel.contains(document.activeElement)) livePanel.focus();

    syncCount();
  }

  // Subtotal, guard and checkout live in a footer that is absent on an empty cart, so it
  // has to be inserted and removed, not only replaced.
  function swapFoot(livePanel, freshPanel) {
    var liveFoot = livePanel.querySelector('.cml-cart__foot');
    var freshFoot = freshPanel.querySelector('.cml-cart__foot');

    if (freshFoot) {
      var incoming = document.importNode(freshFoot, true);
      if (liveFoot) livePanel.replaceChild(incoming, liveFoot);
      else livePanel.appendChild(incoming);
    } else if (liveFoot) {
      livePanel.removeChild(liveFoot);
    }
  }

  // The drawer carries the authoritative count, so the header badge needs no extra request.
  function syncCount() {
    var d = drawer();
    if (!d) return;
    var count = parseInt(d.getAttribute('data-cart-count') || '0', 10);

    document.querySelectorAll('[data-cml-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
    });
    document.querySelectorAll('[data-cml-cart-count-label]').forEach(function (el) {
      el.textContent = count + (count === 1 ? ' item in cart' : ' items in cart');
    });
  }

  /* ------------------------------------------------------------- cart calls */

  function post(url, body) {
    body.sections = SECTION;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw new Error(data.description || data.message || 'Cart error');
        return data;
      });
    });
  }

  function withLoading(button, work) {
    if (busy) return Promise.resolve();
    busy = true;

    var label = button && button.querySelector('[data-cml-btn-label]');
    var original = label && label.textContent;
    if (button) button.disabled = true;
    if (button) button.classList.add('is-loading');
    if (label) label.textContent = 'Adding…';

    return work()
      .catch(function (error) {
        if (window.console) console.error('[cml-cart]', error);
      })
      .then(function () {
        busy = false;
        // The button may have been replaced by the section swap; only restore if it survived.
        if (button && document.contains(button)) {
          button.disabled = false;
          button.classList.remove('is-loading');
          if (label && original != null) label.textContent = original;
        }
      });
  }

  /* ------------------------------------------------------ one copy per line */

  // Every product here is a digital download: a second copy of the same file is never
  // wanted, and the drawer has no quantity control to undo one. So adding means "make sure
  // it is in the cart", never "one more", and no line is ever left above 1.

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function getCart() {
    return fetch(routeRoot() + 'cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    }).then(function (res) {
      return res.json();
    });
  }

  // Adds only the variants that are not in the cart yet — a second click on a buy button,
  // or the order bump still ticked on a re-add, changes nothing — then caps every line.
  function addOnce(items) {
    return getCart()
      .then(function (cart) {
        var inCart = {};
        cart.items.forEach(function (line) {
          inCart[String(line.variant_id)] = true;
        });
        var missing = items.filter(function (item) {
          return !inCart[String(item.id)];
        });
        if (!missing.length) return null;
        return post(routeRoot() + 'cart/add.js', { items: missing });
      })
      .then(capQuantities);
  }

  // Sets any line above 1 back to exactly 1, whatever put it there (an older tab, Horizon's
  // own product form, a shared cart link), and re-renders the drawer from the result. Reads
  // /cart.js rather than the drawer markup, which can be stale when the cart changed
  // elsewhere.
  function capQuantities() {
    return getCart()
      .then(function (cart) {
        var updates = {};
        cart.items.forEach(function (line) {
          if (line.quantity > 1) updates[line.key] = 1;
        });
        if (Object.keys(updates).length) {
          return post(routeRoot() + 'cart/update.js', { updates: updates });
        }
        // Nothing to fix: still hand back fresh drawer markup for the swap.
        return fetch(routeRoot() + '?sections=' + SECTION, { cache: 'no-store' })
          .then(function (res) {
            return res.json();
          })
          .then(function (sections) {
            return { sections: sections };
          });
      })
      .then(function (data) {
        swap(data.sections);
        return data;
      });
  }

  // For openings that are not the tail of our own add (header icon, Horizon's product
  // form): an add already capped the cart, and runs while `busy` is still set.
  function normaliseQuantities() {
    if (busy) return;
    busy = true;
    capQuantities()
      .catch(function (error) {
        if (window.console) console.error('[cml-cart]', error);
      })
      .then(function () {
        busy = false;
      });
  }

  function itemsFromForm(form) {
    var grouped = {};
    new FormData(form).forEach(function (value, key) {
      var match = key.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
      if (!match) return;
      grouped[match[1]] = grouped[match[1]] || {};
      grouped[match[1]][match[2]] = value;
    });

    return Object.keys(grouped)
      .sort()
      .map(function (index) {
        return grouped[index];
      })
      .filter(function (item) {
        return item.id;
      })
      .map(function (item) {
        return { id: item.id, quantity: 1 };
      });
  }

  /* --------------------------------------------------------------- listeners */

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-cml-buyform]');
    if (!form) return;

    var items = itemsFromForm(form);
    if (!items.length) return;

    event.preventDefault();
    var button = form.querySelector('button[type="submit"]');

    withLoading(button, function () {
      return addOnce(items).then(function () {
        open(button);
      });
    });
  });

  document.addEventListener('click', function (event) {
    var openBtn = event.target.closest('[data-cml-cart-open]');
    if (openBtn && drawer()) {
      event.preventDefault();
      open(openBtn);
      return;
    }

    if (event.target.closest('[data-cml-cart-close]')) {
      event.preventDefault();
      close();
      return;
    }

    var add = event.target.closest('[data-cml-cart-add]');
    if (add) {
      event.preventDefault();
      withLoading(add, function () {
        return addOnce([{ id: add.getAttribute('data-cml-cart-add'), quantity: 1 }]);
      });
      return;
    }

    var keepOne = event.target.closest('[data-cml-cart-keepone]');
    if (keepOne) {
      event.preventDefault();
      withLoading(keepOne, function () {
        return post(routeRoot() + 'cart/change.js', {
          line: parseInt(keepOne.getAttribute('data-cml-cart-keepone'), 10),
          quantity: 1,
        }).then(function (data) {
          swap(data.sections);
        });
      });
      return;
    }

    var remove = event.target.closest('[data-cml-cart-remove]');
    if (remove) {
      event.preventDefault();
      withLoading(remove, function () {
        return post('/cart/change.js', {
          line: parseInt(remove.getAttribute('data-cml-cart-remove'), 10),
          quantity: 0,
        }).then(function (data) {
          swap(data.sections);
        });
      });
    }
  });

  document.addEventListener('keydown', function (event) {
    if (!isOpen()) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    var p = panel();
    if (!p) return;
    var items = Array.prototype.filter.call(p.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null;
    });
    if (!items.length) {
      event.preventDefault();
      p.focus();
      return;
    }

    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === p)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* ------------------------------------------------ Horizon action bridge */

  // Horizon's standard-actions-override falls back to navigating to /cart when it can't
  // find its own <theme-drawer id="cart-drawer">. Point openCart at this drawer instead, so
  // add-to-cart from Horizon's own product pages opens it too.
  function bridgeShopifyActions() {
    var actions = window.Shopify && window.Shopify.actions;
    if (!actions || !actions.openCart || !actions.openCart.configure) return;

    actions.openCart.configure({
      handler: function () {
        if (drawer()) {
          open(null);
        } else {
          window.location.href = '/cart';
        }
      },
    });
  }

  if (window.Shopify && window.Shopify.actions) {
    bridgeShopifyActions();
  } else {
    document.addEventListener('DOMContentLoaded', bridgeShopifyActions, { once: true });
  }

  window.CMLCart = { open: open, close: close, refresh: swap };

  syncCount();

  // The drawer is rendered with the page and flags any line above 1; fix it on arrival
  // instead of waiting for the visitor to open the cart.
  if (document.querySelector('[data-cml-cart-overqty]')) normaliseQuantities();
})();
